/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    LinearProgress,
    IconButton,
    Stack,
} from "@mui/material";
import {
    CheckCircle,
    RadioButtonUnchecked,
    Close,
    ArrowForward,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import { fetchCampaigns } from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectIsQuickBooksConnected,
    selectInvoices,
    selectCampaigns,
    selectDunningEvents,
} from "../../redux/selectors/collectSelector";
import { colors } from "../../utility/types/constant";
import { CollectSectionCard } from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// OnboardingChecklist — dashboard widget that shows progress through the
// four key Collect onboarding milestones. Auto-hides once every step is
// done. Dismiss is persisted in localStorage per businessId so it stays
// hidden until they switch workspaces.
// ─────────────────────────────────────────────────────────────────────────

interface ChecklistStep {
    id: string;
    label: string;
    hint: string;
    done: boolean;
    actionLabel: string;
    actionPath: string;
}

const dismissedStorageKey = (businessId: string) =>
    `collect.onboarding.dismissed.${businessId}`;

const OnboardingChecklist: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const businessId = useSelector(selectActiveBusinessId);
    const isConnected = useSelector(selectIsQuickBooksConnected);
    const invoices = useSelector(selectInvoices);
    const campaigns = useSelector(selectCampaigns);
    const events = useSelector(selectDunningEvents);

    const [dismissed, setDismissed] = useState(false);

    // One-shot fetch of campaigns so step 3 reflects real state.
    useEffect(() => {
        if (businessId && (!campaigns || campaigns.length === 0)) {
            dispatch(fetchCampaigns(undefined));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    // Hydrate dismissal flag from localStorage on business change.
    useEffect(() => {
        if (!businessId) return;
        try {
            const flag = localStorage.getItem(dismissedStorageKey(businessId));
            setDismissed(flag === "1");
        } catch {
            setDismissed(false);
        }
    }, [businessId]);

    const steps: ChecklistStep[] = useMemo(
        () => [
            {
                id: "connect_qbo",
                label: "Connect QuickBooks",
                hint: "Link your QuickBooks Online company so we can read invoices.",
                done: !!isConnected,
                actionLabel: "Connect",
                actionPath: "/collect/connect",
            },
            {
                id: "first_sync",
                label: "Run your first sync",
                hint: "Pull customers and invoices from QuickBooks into Collect.",
                done: !!invoices && invoices.length > 0,
                actionLabel: "Sync",
                actionPath: "/collect/connect",
            },
            {
                id: "campaign",
                label: "Review your default campaign",
                hint: "Make sure the right reminder schedule is active.",
                done: !!campaigns && campaigns.length > 0,
                actionLabel: "Open campaigns",
                actionPath: "/collect/campaigns",
            },
            {
                id: "first_reminder",
                label: "Send your first reminder",
                hint: "Run dunning now or fire a manual reminder from any invoice.",
                done: !!events && events.length > 0,
                actionLabel: "Activity",
                actionPath: "/collect/activity",
            },
        ],
        [isConnected, invoices, campaigns, events],
    );

    const doneCount = steps.filter((s) => s.done).length;
    const allDone = doneCount === steps.length;

    if (!businessId || allDone || dismissed) return null;

    const onDismiss = () => {
        if (!businessId) return;
        try {
            localStorage.setItem(dismissedStorageKey(businessId), "1");
        } catch {
            /* ignore quota errors */
        }
        setDismissed(true);
    };

    const onNext = (path: string) => navigate(path);

    return (
        <CollectSectionCard
            sx={{
                p: { xs: 2.5, sm: 3 },
                mb: { xs: 2, sm: 3 },
                border: "1px solid rgba(30, 193, 149, 0.25)",
                backgroundColor: "rgba(30, 193, 149, 0.04)",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexDirection: { xs: "column", sm: "row" },
                    mb: 1.5,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: "1.05rem",
                            color: colors.primary,
                        }}
                    >
                        Get started with Collect
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: "#6b7280",
                            fontSize: "0.82rem",
                            mt: 0.5,
                        }}
                    >
                        {doneCount} of {steps.length} steps complete finish setup to
                        start collecting on autopilot.
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={onDismiss}
                    aria-label="Dismiss onboarding checklist"
                    sx={{ color: "#6b7280" }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            <LinearProgress
                variant="determinate"
                value={(doneCount / steps.length) * 100}
                sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "rgba(30, 193, 149, 0.15)",
                    mb: 2,
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: colors.mainSecondary,
                        borderRadius: 3,
                    },
                }}
            />

            <Stack spacing={1.25}>
                {steps.map((s) => (
                    <Box
                        key={s.id}
                        sx={{
                            display: "flex",
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 1.5,
                            p: 1.25,
                            borderRadius: "10px",
                            backgroundColor: s.done
                                ? "rgba(30, 193, 149, 0.08)"
                                : "#ffffff",
                            border: s.done
                                ? "1px solid rgba(30, 193, 149, 0.2)"
                                : "1px solid rgba(15, 23, 42, 0.06)",
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {s.done ? (
                                <CheckCircle
                                    fontSize="small"
                                    sx={{ color: colors.mainSecondary }}
                                />
                            ) : (
                                <RadioButtonUnchecked
                                    fontSize="small"
                                    sx={{ color: "#9ca3af" }}
                                />
                            )}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 600,
                                        fontSize: "0.9rem",
                                        color: s.done ? "#0f7a5b" : "#1f2937",
                                        textDecoration: s.done ? "line-through" : "none",
                                    }}
                                >
                                    {s.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "0.78rem",
                                        color: "#6b7280",
                                    }}
                                >
                                    {s.hint}
                                </Typography>
                            </Box>
                        </Box>
                        {!s.done && (
                            <IconButton
                                size="small"
                                onClick={() => onNext(s.actionPath)}
                                aria-label={s.actionLabel}
                                sx={{
                                    alignSelf: { xs: "flex-end", sm: "center" },
                                    color: colors.primary,
                                    backgroundColor: "rgba(15, 23, 42, 0.04)",
                                    "&:hover": {
                                        backgroundColor: "rgba(15, 23, 42, 0.08)",
                                    },
                                }}
                            >
                                <ArrowForward fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                ))}
            </Stack>
        </CollectSectionCard>
    );
};

export default OnboardingChecklist;
